/**
 * Builds structured beverage data for the backend.
 * The backend handles the actual mapping to CDP codes.
 * This utility just formats the data structure from UI state.
 */

/**
 * Builds a structured beverage data array from selected preferences.
 * Each category becomes an object with types array containing preference details.
 *
 * additionalDetails/selectedTimeOfDay are keyed by category name alone, not
 * "category_type" — the UI only ever collects one free-text description and
 * one set of time-of-day chips per category (shared across all of that
 * category's selected types), matching how the actual save call to
 * save-beverage-preferences builds its payload elsewhere in these pages.
 *
 * @param {Object} selectedPreferences - Object with category names as keys and arrays of type strings as values
 * @param {Object} additionalDetails - Object with category names as keys and detail strings as values
 * @param {Object} selectedTimeOfDay - Object with category names as keys and arrays of time strings as values
 * @returns {Object} Object with beverageData array and optedOutPersonalization flag
 */
export const buildStructuredBeverageData = (
  selectedPreferences = {},
  additionalDetails = {},
  selectedTimeOfDay = {},
) => {
  const structuredData = [];

  Object.entries(selectedPreferences).forEach(([category, types]) => {
    if (!Array.isArray(types) || types.length === 0) {
      return;
    }

    const categoryData = {
      category,
      types: types.map((type) => ({
        type,
        additionalDetails: additionalDetails[category] || "",
        timeOfDay: selectedTimeOfDay[category] || [],
      })),
    };

    structuredData.push(categoryData);
  });

  return {
    optedOutPersonalization: false,
    beverageData: structuredData,
  };
};

/**
 * Builds a structured beverage data array from category-level details, as used
 * by the BeverageV6/V7 flows: each category has a single additionalDetails/
 * timeOfDay pair shared across all of its selected types (unlike
 * buildStructuredBeverageData above, which keys those per type).
 *
 * @param {string[]} categories - selected category names
 * @param {Object} categoriesDetails - keyed by category name: { types, additionalDetails, timeOfDay }
 * @returns {Array} structuredBeverageData array for the save-beverage-preferences action
 */
export const buildStructuredBeverageDataFromCategoryDetails = (
  categories = [],
  categoriesDetails = {},
) => {
  return categories.map((category) => {
    const entry = categoriesDetails[category] || {
      types: [],
      additionalDetails: "",
      timeOfDay: [],
    };

    return {
      category,
      types: (entry.types || []).map((type) => ({
        type,
        additionalDetails: entry.additionalDetails,
        timeOfDay: entry.timeOfDay,
      })),
    };
  });
};

/**
 * Upserts newEntries into an existing structuredBeverageData array by
 * category name, used by flows (BeverageV3) that save one category at a
 * time and accumulate the full set for the final submit — re-saving a
 * category replaces its previous entry rather than duplicating it.
 *
 * @param {Array} existing - previously accumulated structuredBeverageData
 * @param {Array} newEntries - entries to merge in, keyed by category
 * @returns {Array} merged structuredBeverageData array
 */
export const mergeStructuredBeverageDataByCategory = (
  existing = [],
  newEntries = [],
) => {
  const byCategory = new Map(existing.map((entry) => [entry.category, entry]));
  newEntries.forEach((entry) => byCategory.set(entry.category, entry));
  return Array.from(byCategory.values());
};
